import { useState, useCallback } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BreadcrumbSchema } from "@/components/StructuredData";
import ContactInfo from "@/components/contact/ContactInfo";
import ContactForm from "@/components/contact/ContactForm";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, MapPin } from "lucide-react";

const BREADCRUMBS = [
  { name: "Home", url: "/" },
  { name: "Contact", url: "/contact" }
];

const PROJECT_TYPE_LABELS = {
  loft_conversion: "Loft Conversion",
  house_extension: "House Extension",
  kitchen_renovation: "Kitchen Renovation",
  bathroom: "Bathroom Renovation",
  fitted_wardrobes: "Fitted Wardrobes",
  full_refurbishment: "Full Refurbishment",
  other: "Other"
};

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    projectType: "",
    message: ""
  });
  const [errors, setErrors] = useState({});

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.message.trim()) newErrors.message = "Please tell us about your project";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, formData.phone, formData.message]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    const projectTypeText = formData.projectType ? (PROJECT_TYPE_LABELS[formData.projectType] || formData.projectType) : "Not specified";
    const locationText = formData.address || "Not specified";
    
    const whatsappMessage = `*New Enquiry from Website*\n\n*Name:* ${formData.name}\n*Phone:* ${formData.phone}\n*Location:* ${locationText}\n*Project Type:* ${projectTypeText}\n\n*Message:*\n${formData.message}`;
    const encodedMessage = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/447780996775?text=${encodedMessage}`, '_blank');
    
    setSubmitted(true);
    toast.success("Opening WhatsApp to send your message!");
    setSubmitting(false);
  }, [formData, validateForm]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => prev[name] ? { ...prev, [name]: "" } : prev);
  }, []);

  const handleProjectTypeChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, projectType: value }));
  }, []);

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <main className="max-w-2xl mx-auto px-6 py-32 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-8">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
            Message Ready!
          </h1>
          <p className="text-lg text-stone-600 mb-8">
            WhatsApp should have opened with your message. If it did not open automatically, 
            you can call us directly on <a href="tel:07780996775" className="text-amber-700 font-semibold">07780 996775</a>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => window.location.href = '/'} className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-8">
              Back to Home
            </Button>
            <Button onClick={() => setSubmitted(false)} variant="outline" className="h-12 px-8">
              Send Another Message
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Contact TJP Construction | Free Quote Surrey"
        description="Get a free quote from TJP Construction. Serving Surrey & SW London. Call 07780 996775 or send us a message for extensions, loft conversions & more."
        keywords="contact carpenter surrey, get quote builder, carpentry enquiry sw london, free estimate"
        url="/contact"
      />
      <BreadcrumbSchema items={BREADCRUMBS} />
      <Header />

      <section className="bg-gradient-to-br from-stone-100 to-stone-200 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">Get In Touch</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-6">Contact Us</h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Ready to discuss your project? Get in touch for a free, no-obligation 
            consultation. We are here to help bring your vision to life.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <ContactInfo />
            <ContactForm
              formData={formData}
              errors={errors}
              submitting={submitting}
              onChange={handleChange}
              onProjectTypeChange={handleProjectTypeChange}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </section>

      <section className="bg-stone-200 h-80">
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <MapPin className="mx-auto text-stone-400 mb-4" size={48} />
            <p className="text-stone-500">Serving Surrey & SW London</p>
            <p className="text-stone-400 text-sm">Guildford, Woking, Kingston, Richmond, Wimbledon</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
